import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, NavLink, useLocation } from 'react-router-dom';
import HomePage from './HomePage';
import PickupPage from './PickupPage';
import ContactPage from './ContactPage';
import GamePage from './GamePage';
import ExperiencePage from './ExperiencePage';
import MissionPage from './MissionPage';
import EcoAcademy from './EcoAcademy';
import PrivacyPage from './PrivacyPage';
import TermsPage from './TermsPage';
import BusinessQuotePage from './BusinessQuotePage';
import VisionPage from './VisionPage';
import EcoVersePage from './EcoVersePage';
import HelpHubPage from './HelpHubPage';
import EcoBot from './EcoBot';
import { MobilePickupCTA } from './ChapterNav';
import { track } from './analytics';
import { LangProvider, useLang, Lang } from './i18n';
import MaintenancePage from './MaintenancePage';
import { SITE_MODE, BOOKINGS_OPEN } from './siteMode';
import './App.css';

/** Fires a page_viewed analytics event on every route change (safe no-op until a provider is connected). */
function PageViewTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    track('page_viewed', { path: pathname });
  }, [pathname]);
  return null;
}

/** Smoothly scrolls to a section when the URL hash changes (e.g. /#rewards),
 *  retrying a few times in case the target section hasn't rendered yet. */
function ScrollToHash() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    let tries = 0;
    let timer: ReturnType<typeof setTimeout>;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (tries++ < 12) {
        timer = setTimeout(tryScroll, 60);
      }
    };
    timer = setTimeout(tryScroll, 60);
    return () => clearTimeout(timer);
  }, [pathname, hash]);
  return null;
}

/** On every route change (without a #hash), start the new page at the very top — instantly. */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    // 'instant' explicitly bypasses the site's CSS smooth-scrolling
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname, hash]);
  return null;
}

/** Thin gradient bar at the very top showing scroll progress. */
function ScrollProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const max = el.scrollHeight - el.clientHeight;
      setPct(max > 0 ? (el.scrollTop / max) * 100 : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);
  return <div className="scroll-progress" style={{ width: pct + '%' }} />;
}

/** Routes wrapped with an entrance animation that replays on every navigation. */
function AnimatedRoutes() {
  const location = useLocation();
  return (
    <div className="route-anim" key={location.pathname}>
      <Routes location={location}>
        <Route path="/" element={<HomePage />} />
        <Route path="/pickup" element={<PickupPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/game" element={<GamePage />} />
        <Route path="/experience" element={<ExperiencePage />} />
        <Route path="/mission" element={<MissionPage />} />
        <Route path="/learn" element={<EcoAcademy />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/business-quote" element={<BusinessQuotePage />} />
        <Route path="/vision" element={<VisionPage />} />
        <Route path="/ecoverse" element={<EcoVersePage />} />
        <Route path="/help" element={<HelpHubPage />} />
      </Routes>
    </div>
  );
}

function AppInner() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();
  /** Close the mobile menu; if the link points at the page we're already on, glide to the top. */
  const closeMenu = () => setMenuOpen(false);
  const navTo = (path: string) => () => {
    setMenuOpen(false);
    if (location.pathname === path) window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const isFilm = location.pathname === '/experience';
  const { lang, setLang, t } = useLang();
  const pickLang = (l: Lang) => { setLang(l); setLangOpen(false); };

  /** On-page section links (Impact/Rewards): scroll immediately, every click. */
  const scrollToSection = (id: string) => (e: React.MouseEvent) => {
    closeMenu();
    const el = document.getElementById(id);
    if (el) {
      e.preventDefault();
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.replaceState(null, '', '/#' + id);
    }
    // if the section isn't on this page, let the Link navigate home; ScrollToHash handles it
  };

  return (
    <>
      <ScrollToHash />
      <ScrollToTop />
      <PageViewTracker />
      {!isFilm && <ScrollProgress />}
      {!isFilm && <MobilePickupCTA />}
      <div className="App">
        {/* ── Navbar (hidden during the cinematic Experience) ── */}
        {!isFilm && <nav className="navbar">
          <div className="nav-container">
            <Link to="/" className="nav-logo-link" onClick={closeMenu}>
              <img src="/logo-main.png" className="nav-logo" alt="Everything Eco NYC" />
            </Link>

            <div className="nav-lang">
              <button
                className="nav-globe"
                aria-label="Change language / Cambiar idioma"
                aria-expanded={langOpen}
                onClick={() => setLangOpen((o) => !o)}
              >
                🌐 <span>{lang.toUpperCase()}</span>
              </button>
              {langOpen && (
                <div className="nav-lang-panel" role="menu">
                  <button role="menuitem" className={lang === 'en' ? 'on' : ''} onClick={() => pickLang('en')}>
                    <strong>English</strong><span>United States</span>
                  </button>
                  <button role="menuitem" className={lang === 'es' ? 'on' : ''} onClick={() => pickLang('es')}>
                    <strong>Español</strong><span>Nueva York</span>
                  </button>
                </div>
              )}
            </div>

            <button
              className="nav-toggle"
              aria-label="Toggle menu"
              onClick={() => setMenuOpen((o) => !o)}
            >
              {menuOpen ? '✕' : '☰'}
            </button>

            <div className={`nav-links${menuOpen ? ' open' : ''}`}>
              <NavLink to="/" end className="nav-item" onClick={navTo('/')}>{t('nav.home')}</NavLink>
              <NavLink to="/business-quote" className="nav-item" onClick={navTo('/business-quote')}>{t('nav.business')}</NavLink>
              <NavLink to="/mission" className="nav-item" onClick={navTo('/mission')}>{t('nav.mission')}</NavLink>
              <NavLink to="/learn" className="nav-item" onClick={navTo('/learn')}>{t('nav.learn')}</NavLink>
              <Link to="/#impact" className="nav-item" onClick={scrollToSection('impact')}>{t('nav.impact')}</Link>
              <Link to="/#rewards" className="nav-item" onClick={scrollToSection('rewards')}>{t('nav.rewards')}</Link>
              <NavLink to="/game" className="nav-item" onClick={navTo('/game')}>{t('nav.play')}</NavLink>
              <NavLink to="/contact" className="nav-item" onClick={navTo('/contact')}>{t('nav.contact')}</NavLink>
              <Link to="/pickup" className="nav-cta" onClick={navTo('/pickup')}>{BOOKINGS_OPEN ? t('nav.cta') : t('nav.waitlist')}</Link>
            </div>
          </div>
        </nav>}

        {/* ── Routes (with page transition) ── */}
        {!isFilm && lang === 'es' && location.pathname !== '/' && (
          <div className="es-notice">
            🌐 Esta página todavía está en inglés — estamos traduciendo todo el sitio, poco a poco. 💚
          </div>
        )}
        <AnimatedRoutes />

        {/* ── Footer ── */}
        {!isFilm && <footer className="ee-footer">
          <div className="ee-footer-inner">
            <div className="ee-footer-top">
              <div className="ee-footer-brand">
                <img src="/logo-main.png" alt="Everything Eco" className="ee-footer-logo" />
                <p>{t('footer.tagline')}</p>
              </div>
              <div className="ee-footer-col">
                <h4>{t('footer.services')}</h4>
                <Link to="/pickup">{t('footer.electronics')}</Link>
                <Link to="/pickup">{t('footer.textile')}</Link>
                <Link to="/pickup">{t('footer.junk')}</Link>
                <Link to="/pickup">{t('footer.schedule')}</Link>
                <Link to="/business-quote">{t('footer.bizquotes')}</Link>
              </div>
              <div className="ee-footer-col">
                <h4>{t('footer.company')}</h4>
                <Link to="/help">🧭 Get Help / Opportunity Hub</Link>
                <Link to="/mission">{t('footer.mission')}</Link>
                <Link to="/vision">{t('footer.vision')}</Link>
                <Link to="/ecoverse">EcoVerse 🌌</Link>
                <Link to="/learn">{t('footer.academy')}</Link>
                <Link to="/privacy">{t('footer.privacy')}</Link>
                <Link to="/terms">{t('footer.terms')}</Link>
                <Link to="/#impact">{t('footer.impact')}</Link>
                <Link to="/contact">{t('footer.contact')}</Link>
              </div>
              <div className="ee-footer-col">
                <h4>{t('footer.touch')}</h4>
                <a href="mailto:everythingeconyc@gmail.com" onClick={() => track('email_clicked', { source: 'footer' })}>everythingeconyc@gmail.com</a>
                <a href="https://instagram.com/everythingeconyc" target="_blank" rel="noreferrer" onClick={() => track('social_clicked', { network: 'instagram' })}>📸 @everythingeconyc</a>
                <span>Williamsburg, Brooklyn</span>
                <span>New York City</span>
              </div>
            </div>
            <div className="ee-footer-bottom">
              <p>© {new Date().getFullYear()} Everything Eco NYC. {t('footer.rights')}</p>
              <p>{t('footer.slogan')}</p>
            </div>
          </div>
        </footer>}

        {!isFilm && <EcoBot />}
      </div>
    </>
  );
}

function App() {
  // Full-site maintenance mode (REACT_APP_SITE_MODE=maintenance)
  if (SITE_MODE === 'maintenance') return <MaintenancePage />;
  return (
    <Router>
      <LangProvider>
        <AppInner />
      </LangProvider>
    </Router>
  );
}

export default App;
